// src/services/companyService.js
import { EDEFTER_API_URL } from '../config/edefterApi';
import AuthService from './authService';

class CompanyService {
  /**
   * Create a new company
   * @param {Object} companyData - Company data
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  static async createCompany(companyData) {
    try {
      const response = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/api/company/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(companyData),
      });

      if (!response) {
        return {
          success: false,
          error: 'Session expired. Please sign in again.',
        };
      }

      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = text || 'Unknown error';
      }

      if (response.ok) {
        return {
          success: true,
          data: data,
        };
      } else {
        // Handle error response - could be string or object
        let errorMessage = 'Failed to create company.';
        if (typeof data === 'string') {
          errorMessage = data;
        } else if (data && typeof data === 'object') {
          errorMessage = data.message || data.error || data.status || JSON.stringify(data);
        }
        return {
          success: false,
          error: errorMessage,
        };
      }
    } catch (error) {
      console.error('Create company error:', error);
      return {
        success: false,
        error: error.message || 'Failed to create company.',
      };
    }
  }

  /**
   * Get companies by user ID
   * @param {string} userId - User ID
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  static async getCompaniesByUserId(userId) {
    try {
      const response = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/api/company/list?userId=${userId}`);

      if (!response) {
        return {
          success: false,
          error: 'Session expired. Please sign in again.',
        };
      }
      
      if (response.ok) {
        const data = await response.json();
        return {
          success: true,
          data: data,
        };
      } else {
        return {
          success: false,
          error: 'Failed to load companies.',
        };
      }
    } catch (error) {
      console.error('Get companies error:', error);
      return {
        success: false,
        error: error.message || 'Failed to load companies.',
      };
    }
  }

  /**
   * Get company by ID
   * @param {string} companyId - Company ID
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  static async getCompanyById(companyId) {
    try {
      const response = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/api/company/${companyId}`);

      if (!response) {
        return {
          success: false,
          error: 'Session expired. Please sign in again.',
        };
      }
      
      if (response.ok) {
        const data = await response.json();
        return {
          success: true,
          data: data,
        };
      } else if (response.status === 404) {
        return {
          success: false,
          error: 'Company not found.',
        };
      } else {
        return {
          success: false,
          error: 'Failed to load company.',
        };
      }
    } catch (error) {
      console.error('Get company by id error:', error);
      return {
        success: false,
        error: error.message || 'Failed to load company.',
      };
    }
  }

  /**
   * Check if company exists by tax ID (vergiKimlikNo) and user ID
   * @param {string} userId - User ID
   * @param {string} vergiKimlikNo - Tax ID
   * @returns {Promise<{success: boolean, exists: boolean, data?: Object, error?: string}>}
   */
  static async checkCompanyExists(userId, vergiKimlikNo) {
    try {
      // Kullanıcının company listesini çek
      const companiesResult = await this.getCompaniesByUserId(userId);
      
      if (companiesResult.success && companiesResult.data) {
        // Vergi kimlik numarasına göre kontrol et
        const existingCompany = companiesResult.data.find(
          company => company.vergiKimlikNo === vergiKimlikNo
        );
        
        if (existingCompany) {
          return {
            success: true,
            exists: true,
            data: existingCompany,
          };
        }
      }
      
      return {
        success: true,
        exists: false,
      };
    } catch (error) {
      console.error('Check company exists error:', error);
      return {
        success: false,
        exists: false,
        error: error.message || 'Failed to check company existence.',
      };
    }
  }

  /**
   * Update company
   * @param {string} companyId - Company ID
   * @param {Object} companyData - Updated company data
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  static async updateCompany(companyId, companyData) {
    try {
      const response = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/api/company/${companyId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(companyData),
      });

      if (!response) {
        return {
          success: false,
          error: 'Session expired. Please sign in again.',
        };
      }

      const data = await response.json();

      if (response.ok) {
        return {
          success: true,
          data: data,
        };
      } else {
        return {
          success: false,
          error: data || 'Failed to update company.',
        };
      }
    } catch (error) {
      console.error('Update company error:', error);
      return {
        success: false,
        error: error.message || 'Failed to update company.',
      };
    }
  }
}

export default CompanyService;

